import React, { useState, useEffect } from 'react'
import {
    Paper,
    TableContainer,
    Table,
    TableBody,
    TableRow,
    TableCell,
    TablePagination,
    IconButton,
    CircularProgress,
    Tooltip,
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { tableStyles } from './TableStyles'
import moment from 'moment'
import EnhancedTableHead from './EnhancedTableHead'
import { Data, Order } from '../HistoryTypes'
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf'
import EmailIcon from '@material-ui/icons/Email'
import { useSelector, useDispatch } from 'react-redux'
import { fetchSessions, sortByProperty, sortByOrder, sendInvoiceForCharging } from '../../../redux/actions/sessions'
import { ChargerConnectorType, ChargerConnectorSocketType } from '../../../shared/types/charger'
import { config, getToken } from '../../../config'
import TimelineIcon from '@material-ui/icons/Timeline'
import { exportFile } from '../../../shared/utils/export'
import { ChargingInitSources, PaymentInterval, PaymentStatus } from '../../../shared/types/chargingSession'
import FlexiPlanDialog from '../FlexiPlanDialog'
import { useTenantFilter } from '../../../utils/tenantFilter'
import CopyableSessionId from '../CopyableSessionId'

export default function HistoryTable() {
    const { t } = useTranslation()
    const classes = tableStyles()
    const dispatch = useDispatch()

    const [order, setOrder] = useState<Order>('desc')
    const [orderBy, setOrderBy] = useState<keyof Data>('startedAt')
    const [page, setPage] = useState(0)
    const [rowsPerPage] = useState(30)
    const [dataKey, setDataKey] = useState<number>(0)
    const [selectedFlexiSession, setSelectedFlexiSession] = useState<any>(null)
    const sessionsState = useSelector((state: any) => state.sessions.items)
    const filter = useSelector((state: any) => state.sessions.filter)
    const sort = useSelector((state: any) => state.sessions.sort)
    const tenantId = useSelector((state: any) => state.login.value.user.tenantId)
    const tenantFilter = useTenantFilter()

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Data) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
        dispatch(sortByProperty(property))
        dispatch(sortByOrder(isAsc ? 'desc' : 'asc'))
    }
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
        fetchData(newPage)
    }

    const fetchData = (page?: number) => {
        dispatch(fetchSessions(dataKey, sessionsState, Number(page) + 1, rowsPerPage, { ...filter, tenantId: tenantFilter }, sort))
    }

    useEffect(() => {
        if (sessionsState.executed && sessionsState.key !== dataKey) {
            fetchData()
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, dataKey, sessionsState, filter, sort])

    useEffect(() => {
        setDataKey(dataKey + 1)
        // pri zmene filtra tenanta sa zoznam nacita znova od 1. strany
        setPage(0)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, sort, tenantFilter])

    const onExport = (id: string) => {
        exportFile(config.proxy, config.api, `ChargingSession/invoice/${id}`, getToken(), true)
    }

    const onSend = (id: string) => {
        dispatch(sendInvoiceForCharging(id))
    }

    const onFlexiPlanOpen = (row: any) => {
        setSelectedFlexiSession(row)
    }

    const onFlexiPlanClose = () => {
        setSelectedFlexiSession(null)
    }

    const determineUserType = (row: any) => {
        if (row.idTag && row.whitelistAuthentication) return t('users.whitelist')
        else if (!row.user) return t('users.anonymous')
        return t('users.standard')
    }

    // typ zasuvky pre tooltip v stlpci Connector (AC/DC je uz zobrazene priamo v bunke)
    const determineConnectorSocketType = (row: any) => {
        let socket: string
        switch (row.chargingConnector.socketType) {
            case ChargerConnectorSocketType.Type1: {
                socket = t('locations.connectorSocketTypeType1')
                break
            }
            case ChargerConnectorSocketType.Type2: {
                socket = t('locations.connectorSocketTypeType2')
                break
            }
            case ChargerConnectorSocketType.CHAdeMO: {
                socket = t('locations.connectorSocketTypeChademo')
                break
            }
            case ChargerConnectorSocketType.CCS: {
                socket = t('locations.connectorSocketTypeCcs')
                break
            }
            default: {
                socket = t('common.none')
                break
            }
        }
        return socket
    }

    const determineChargingInitSource = (row: any) => {
        let chargingInitSource: string
        switch (row.chargingInitSource) {
            case ChargingInitSources.SEAK: {
                chargingInitSource = 'SEAK'
                break
            }
            case ChargingInitSources.HUBJECT: {
                chargingInitSource = 'HUBJECT'
                break
            }
            case ChargingInitSources.VILLAPRO: {
                chargingInitSource = 'VILLAPRO'
                break
            }
            case ChargingInitSources.PLUGNCHARGE: {
                chargingInitSource = 'PLUGNCHARGE'
                break
            }
            case ChargingInitSources.QVEND: {
                chargingInitSource = 'QVEND'
                break
            }
            default: {
                chargingInitSource = t('common.none')
                break
            }
        }
        return chargingInitSource
    }

    const determinePaymentStatus = (row: any) => {
        let paymentStatus: string
        switch (row.paymentStatus) {
            case PaymentStatus.FREE: {
                paymentStatus = 'FREE'
                break
            }
            case PaymentStatus.PAID: {
                paymentStatus = 'PAID'
                break
            }
            case PaymentStatus.UNPAID: {
                paymentStatus = 'UNPAID'
                break
            }
            case PaymentStatus.ROAMING: {
                paymentStatus = 'ROAMING'
                break
            }
            default: {
                paymentStatus = t('common.none')
                break
            }
        }
        return paymentStatus
    }
    const determinePaymentInterval = (row: any) => {
        let paymentInterval: string
        switch (row.paymentInterval) {
            case PaymentInterval.INSTANT: {
                paymentInterval = 'INSTANT'
                break
            }
            case PaymentInterval.MONTHLY: {
                paymentInterval = 'MONTHLY'
                break
            }
            case PaymentInterval.ROAMING: {
                paymentInterval = 'ROAMING'
                break
            }
            default: {
                paymentInterval = t('common.none')
                break
            }
        }
        return paymentInterval
    }

    return (
        <Paper className={classes.paper}>
            <TableContainer style={{ height: '100%', overflowY: 'scroll' }}>
                <Table stickyHeader className={classes.table} aria-labelledby="tableTitle" aria-label="enhanced table" style={{ whiteSpace: 'nowrap' }}>
                    <EnhancedTableHead
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                        rowCount={sessionsState.value.count}
                    />
                    {!sessionsState.executed ? (
                        <TableCell colSpan={8} align="center" className={classes.circularProgressCell}>
                            <div className={classes.circularProgressWrapper}>
                                <CircularProgress size={40} />
                            </div>
                        </TableCell>
                    ) : (
                        <TableBody style={{ width: '100%' }}>
                            {sessionsState.value.items?.map((row: any) => {
                                if (
                                    row.location != null &&
                                    row.charger != null &&
                                    row.chargingConnectorId != null &&
                                    row.startedAt
                                ) {
                                    const customKey = `${row.location?.id}${row.charger?.id}${row.chargingConnectorId?.id}${row.startedAt}`
                                    return (
                                        <TableRow key={customKey}>
                                            <TableCell align="left">
                                                {row.location.name}
                                                <div>
                                                    <CopyableSessionId id={row.id} />
                                                </div>
                                            </TableCell>
                                            <TableCell align="left">{row.charger.name}</TableCell>
                                            {/* cislo konektora na nabijacke (ExternalId) + AC/DC; plny typ konektora je v tooltipe */}
                                            <TableCell align="center">
                                                <Tooltip title={determineConnectorSocketType(row)}>
                                                    <span>
                                                        {row.connectorExternalId ?? row.chargingConnector?.externalId ?? '-'}
                                                        {row.chargingConnector
                                                            ? ` (${row.chargingConnector.type === ChargerConnectorType.Ac
                                                                ? t('locations.connectorTypeAc')
                                                                : t('locations.connectorTypeDc')})`
                                                            : ''}
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell align="left">{moment(row.startedAt).format('DD.MM.YYYY | HH:mm')}</TableCell>
                                            <TableCell align="center">{row.consumedWh / 1000}</TableCell>
                                            <TableCell align="center">
                                                {row.endedAt ? row.length : t('users.sessionInProgress')}
                                            </TableCell>
                                            <TableCell align="center">
                                                {row.endedAt
                                                    ? `${Number((row.chargingPrice + row.parkingPrice) || row.price).toFixed(2)} €`
                                                    : t('users.sessionInProgress')
                                                }
                                            </TableCell>
                                            <TableCell align="left">{row.pricingCondition?.name}</TableCell>
                                            {/* <TableCell align="left">{row.oldPricelistInfo}</TableCell> */}
                                            <TableCell align="left">{row.user?.email ?? t('common.none')}</TableCell>
                                            <TableCell align="left">
                                                {row.chargingInitSource === ChargingInitSources.HUBJECT
                                                    ? (row.hjIdentificationValue ?? t('common.none'))
                                                    : (row.idTag ?? t('common.none'))}
                                            </TableCell>
                                            {/* <TableCell align="left">
                                                {row.car?.carLicensePlate ?? t('common.none')}
                                            </TableCell> */}
                                            <TableCell align="left">{determineUserType(row)}</TableCell>
                                            <TableCell align="left">{determineChargingInitSource(row)}</TableCell>
                                            <TableCell align="center">{determinePaymentStatus(row)}</TableCell>
                                            <TableCell align="center">{determinePaymentInterval(row)}</TableCell>
                                            <TableCell align="center">
                                                {(row.flexiChargingSchedule != null || row.flexiChargingScheduleId != null) && (
                                                    <IconButton onClick={() => onFlexiPlanOpen(row)}>
                                                        <TimelineIcon />
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton
                                                    onClick={() => onExport(row.id)}
                                                    disabled={
                                                        !row.endedAt ||
                                                        determineChargingInitSource(row) !== 'SEAK' ||
                                                        !row.hasInvoice
                                                    }
                                                >
                                                    <PictureAsPdfIcon />
                                                </IconButton>
                                            </TableCell>
                                            {tenantId && tenantId === config.mainTenantId && (
                                                <TableCell align="center">
                                                    <IconButton
                                                        onClick={() => onSend(row.id)}
                                                        disabled={
                                                            !row.endedAt ||
                                                            determineChargingInitSource(row) !== 'SEAK' ||
                                                            !row.hasInvoice
                                                        }
                                                    >
                                                        <EmailIcon />
                                                    </IconButton>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    )
                                }
                            })}
                        </TableBody>
                    )}
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[30]}
                component={Paper}
                count={sessionsState.value.count}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                className={classes.pagination}
            />
            <FlexiPlanDialog
                open={selectedFlexiSession != null}
                onClose={onFlexiPlanClose}
                session={selectedFlexiSession}
            />
        </Paper>
    )
}
